<template>
    <div
        class="faq-list"
    >
        <!-- Progress Indicator -->
        <v-slide-y-transition>
            <div
                v-if="!lo_isError(faqObj) && !faqObj?.data"
                class="d-flex justify-center mb-16"
            >
                <v-progress-circular indeterminate color="accent" width="6" size="48"></v-progress-circular>
            </div>
        </v-slide-y-transition>

        <!-- Error Loading FAQ -->
        <v-row
            v-if="lo_isError(faqObj)"
        >
            <v-col>
                <v-alert tile type="error">There was an error loading the FAQ.</v-alert>
            </v-col>
        </v-row>

        <!-- FAQ Section -->
        <div
            v-if="!lo_isError(faqObj) && faqObj?.data"
            class="mt-4"
        >
            <div
                class="d-flex gap-2"
            >
                <v-text-field
                    v-model="searchTerm"
                    label="Search FAQs"
                    density="compact"
                    @update:model-value="onFilter"
                    slim
                    hide-details
                    clearable
                    persistent-clear
                ></v-text-field>
                <v-menu>
                    <template #activator="{ props }">
                        <v-btn
                            v-bind="props"
                            color="secondary"
                            variant="flat"
                            size="large"
                            title="Jump to Section"
                            slim
                        >
                            <v-icon
                                :icon="mdiListBoxOutline"
                                size="32"
                            ></v-icon>
                            <v-icon
                                class="ml-1"
                                :icon="mdiChevronDown"
                                size="24"
                            ></v-icon>
                        </v-btn>
                    </template>
                    <v-list>
                        <v-sheet
                            class="text-center mt-n2 pa-1"
                            color="secondary"
                        >
                            JUMP TO SECTION
                        </v-sheet>
                        <v-list-item
                            v-for="(group, index) in faqObj.data"
                            :key="`faq-group-${group.uid}`"
                            @click="onGoToSection(group.uid)"
                        >
                            <v-list-item-title>
                                {{ group.name }}
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>

            <v-divider
                class="mt-10 mb-6"
            ></v-divider>

            <div>
                <section
                    v-for="(group, index) in filteredItems"
                    :key="`faq-group-${group.uid}`"
                    :id="`faq-group-${group.uid}`"
                >
                    <h2
                        class="mb-3"
                    >
                        {{ group.name }}
                    </h2>
                    <v-expansion-panels
                        elevation="0"
                    >
                        <v-expansion-panel
                            v-for="(item, itemIndex) in group.faqs"
                            :key="`faq-item-${item.uid}`"
                            static
                        >
                            <v-expansion-panel-title
                                class="text-h6 font-weight-medium"
                            >
                                <span class="line-height-4">
                                    {{ item.question }}
                                </span>
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <div
                                    v-html="item.answer"
                                    class="font-size-6"
                                ></div>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </section>
            </div>
        </div>

        <ScrollTopBtn></ScrollTopBtn>
    </div>
</template>

<script lang="ts" setup>
    import type {
        ContentFaqGroup,
        PaginatedPayload
    } from '~/types';
    import {
        useApiCommon
    } from '~/composables';
    import { isError as lo_isError } from 'es-toolkit';
    import {
        mdiChevronDown,
        mdiListBoxOutline
    } from '@mdi/js';

    const ScrollTopBtn = defineAsyncComponent(() => import('~/components/ScrollTopBtn/ScrollTopBtn.vue'));

    const props = defineProps<{
        type: string[];
    }>();

    const useApiCommonObj = useApiCommon();
    const key = computed(() => `faq-main-${props.type.join('-')}`);
    const filteredItems = useState<ContentFaqGroup[]>(`${key}_filtered`, () => []);
    const searchTerm = ref<Nilish<string>>();

    async function getData(): Promise<PaginatedPayload<ContentFaqGroup> | Error> {
        const response = await useApiCommonObj.getPagedListItems<ContentFaqGroup>('company/faq-groups/');

        if (!lo_isError(response) && response?.data) {
            filteredItems.value = response.data;
        }

        return response;
    }

    const {
        data: faqObj
    } = useLazyAsyncData<PaginatedPayload<ContentFaqGroup> | Error>(
        key,
        () => getData()
    );

    function onFilter(): void {
        if (lo_isError(faqObj.value) || !faqObj.value?.data) {
            filteredItems.value = [];
            return;
        }

        if (!searchTerm.value) {
            filteredItems.value = faqObj.value.data;
            return;
        }

        const f = searchTerm.value.toLowerCase();
        filteredItems.value = faqObj.value.data.reduce(
            (acc: ContentFaqGroup[], group) => {
                const matchedFaqs = group.faqs?.filter((faq) => {
                    return faq.question.toLowerCase().includes(f) || faq.answer.toLowerCase().includes(f);
                });

                if (matchedFaqs && matchedFaqs.length) {
                    acc.push({
                        ...group,
                        faqs: matchedFaqs
                    });
                }

                return acc;
            },
            []
        );
    }

    async function onGoToSection(id: string): Promise<void> {
        searchTerm.value = '';
        onFilter();
        await nextTick();
        document.querySelector(`#faq-group-${id}`)
            ?.scrollIntoView({ behavior: 'smooth' });
    }
</script>

<style lang="scss">
.faq-list {
    section {
        scroll-margin-top: 3.6rem;
    }

    section + section {
        margin-top: 2.4rem;
    }

    margin-bottom: 2.4rem;
}
</style>
